import { useLiveQuery } from 'dexie-react-hooks'
import { db, type Transaction } from '../db/database'
import { plannedTags } from '../db/budget'
import { formatDay, monthOf } from '../lib/dates'
import { formatAriary } from '../lib/money'
import { TransactionRow } from '../components/TransactionRow'
import { PendingConfirmations } from '../components/PendingConfirmations'
import { MonthMark } from '../components/MonthMark'
import './TransactionsScreen.css'

/** L'historique complet, groupé par jour. Gratuit et sans limite (feature 4). */
export function TransactionsScreen(
  { walletId, onOpen, onEdit, onDelete }: {
    walletId: string
    onOpen?: (t: Transaction) => void
    onEdit?: (t: Transaction) => void
    onDelete?: (t: Transaction, label: string) => void
  },
) {
  const data = useLiveQuery(async () => {
    const transactions = (await db.transactions.where({ wallet_id: walletId }).toArray())
      .filter((t) => !t.deleted_at)
    const categories = await db.categories.where({ wallet_id: walletId }).toArray()
    return { transactions, categories, plans: await plannedTags(walletId) }
  }, [walletId])

  if (!data) return <div className="screen-loading" />

  const byId = new Map(data.categories.map((c) => [c.id, c]))

  if (data.transactions.length === 0) {
    return (
      <div className="screen">
        <PendingConfirmations walletId={walletId} scope="cycle" />
        <p className="empty">Rien à afficher pour l'instant.</p>
      </div>
    )
  }

  // Groupement par jour, du plus récent au plus ancien.
  const days = new Map<string, typeof data.transactions>()
  for (const transaction of [...data.transactions].sort(
    (a, b) => b.operation_date.localeCompare(a.operation_date) ||
              // L'identifiant et non `client_updated_at` : ce dernier est
              // réécrit à CHAQUE modification, et corriger un montant faisait
              // remonter la ligne en tête de sa journée. Les UUIDv7 portent
              // leur date de création, qui ne bouge jamais.
              b.id.localeCompare(a.id),
  )) {
    const list = days.get(transaction.operation_date) ?? []
    list.push(transaction)
    days.set(transaction.operation_date, list)
  }

  let previousMonth = ''

  return (
    <div className="screen">
      {/* Tout ce qui reste à confirmer sur le cycle, journées passées incluses :
          c'est ici qu'on vient rattraper ses oublis. */}
      <PendingConfirmations walletId={walletId} scope="cycle" />

      {[...days].map(([day, transactions]) => {
        const month = monthOf(day)
        const newMonth = month !== previousMonth
        previousMonth = month
        const dayTotal = transactions.reduce(
          (sum, t) => sum + (t.direction === 'income' ? t.amount : -t.amount), 0,
        )

        return (
          <section key={day} className="day">
            {newMonth && <MonthMark date={day} />}
            <header className="day-head">
              <h2>{formatDay(day)}</h2>
              <span className="tabular">{formatAriary(dayTotal)}</span>
            </header>
            <ul className="rows">
              {transactions.map((transaction) => (
                <TransactionRow
                  key={transaction.id}
                  transaction={transaction}
                  category={byId.get(transaction.category_id ?? '')}
                  plan={data.plans.get(transaction.id)}
                  onOpen={onOpen}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))}
            </ul>
          </section>
        )
      })}
    </div>
  )
}
